import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useAuth } from '../stores/auth';
import api from '../lib/api';
import {
  ArrowLeft,
  User,
  Mail,
  Shield,
  LogOut,
  Bell,
  Download,
  CheckCircle,
  Calendar,
  TrendingUp,
  Info,
} from 'lucide-react';
import { VERSION } from '../lib/version';
import { TENANT_NAME } from '../lib/tenant';

interface UserStats {
  visits_today: number;
  visits_this_week: number;
  visits_this_month: number;
  avg_perfect_store_score: number;
  total_photos: number;
  total_checklists: number;
}

export default function Profile() {
  const [, setLocation] = useLocation();
  const { user, logout } = useAuth();
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    localStorage.getItem('notificationsEnabled') === 'true'
  );
  const [offlineMode, setOfflineMode] = useState(
    localStorage.getItem('offlineMode') === 'true'
  );

  // Fetch user statistics
  const { data: stats, isLoading: isLoadingStats } = useQuery<UserStats>({
    queryKey: ['user', 'stats'],
    queryFn: () => api.get('/users/me/stats').then(res => res.data.data),
  });

  const handleLogout = async () => {
    if (confirm('Deseja realmente sair do aplicativo?')) {
      await logout();
      setLocation('/login');
    }
  };

  const toggleNotifications = () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    localStorage.setItem('notificationsEnabled', String(newValue));

    if (newValue && 'Notification' in window) {
      Notification.requestPermission();
    }
  };

  const toggleOfflineMode = () => {
    const newValue = !offlineMode;
    setOfflineMode(newValue);
    localStorage.setItem('offlineMode', String(newValue));
  };

  const getRoleName = (role: string) => {
    const roles: Record<string, string> = {
      ADMIN: 'Administrador',
      MANAGER: 'Gerente',
      SUPERVISOR: 'Supervisor',
      PROMOTER: 'Promotor',
      VIEWER: 'Visualizador',
    };
    return roles[role] || role;
  };

  const appVersion = VERSION.gitCommitShort;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4 pb-24">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation('/')}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Meu Perfil</h1>
          </div>
        </div>
      </header>

      {/* Profile Card */}
      <div className="px-4 -mt-16 mb-4">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-8 w-8 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">{user?.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Shield className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {getRoleName(user?.role || '')}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-gray-600">
            <Mail className="h-4 w-4" />
            <span className="text-sm">{user?.email}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 p-4 space-y-4">
        {/* Statistics */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h2 className="font-semibold text-gray-900">Estatísticas</h2>
            </div>
          </div>

          {isLoadingStats ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Carregando...</p>
            </div>
          ) : stats ? (
            <div className="grid grid-cols-2 gap-4 p-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">
                  {stats.visits_today}
                </p>
                <p className="text-xs text-gray-600 mt-1">Visitas Hoje</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">
                  {stats.visits_this_week}
                </p>
                <p className="text-xs text-gray-600 mt-1">Esta Semana</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">
                  {stats.visits_this_month}
                </p>
                <p className="text-xs text-gray-600 mt-1">Este Mês</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">
                  {stats.avg_perfect_store_score}%
                </p>
                <p className="text-xs text-gray-600 mt-1">Score Médio</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total_photos}
                </p>
                <p className="text-xs text-gray-600 mt-1">Fotos Enviadas</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total_checklists}
                </p>
                <p className="text-xs text-gray-600 mt-1">Checklists</p>
              </div>
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">
              <p className="text-sm">Estatísticas não disponíveis</p>
            </div>
          )}
        </div>

        {/* Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Configurações</h2>
          </div>

          <div className="divide-y divide-gray-100">
            {/* Notifications Toggle */}
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <Bell className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Notificações</p>
                  <p className="text-xs text-gray-500">
                    Receber alertas de novas tarefas
                  </p>
                </div>
              </div>
              <button
                onClick={toggleNotifications}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  notificationsEnabled ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    notificationsEnabled ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Offline Mode Toggle */}
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100">
                  <Download className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Modo Offline</p>
                  <p className="text-xs text-gray-500">
                    Salvar dados localmente
                  </p>
                </div>
              </div>
              <button
                onClick={toggleOfflineMode}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  offlineMode ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    offlineMode ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* App Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-gray-400" />
              <h3 className="font-medium text-gray-900">Informações do App</h3>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Versão</span>
              <span className="font-medium text-gray-900">{appVersion}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Plataforma</span>
              <span className="font-medium text-gray-900">PWA</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Branch</span>
              <span className="font-medium text-gray-900">{VERSION.gitBranch}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Build</span>
              <span className="font-medium text-gray-900">
                {new Date(VERSION.buildTime).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20 p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">
                Continue assim!
              </h3>
              <p className="text-sm text-gray-700">
                {stats?.visits_this_week || 0} visitas esta semana.
                {stats && stats.visits_this_week >= 5
                  ? ' Excelente trabalho!'
                  : ' Continue visitando mais lojas para melhorar seus resultados.'}
              </p>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-4 bg-red-600 text-white rounded-xl font-semibold active:bg-red-700"
        >
          <LogOut className="h-5 w-5" />
          Sair do Aplicativo
        </button>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-xs text-gray-500">
            Prisma - Trade Marketing & Merchandising
          </p>
          <p className="text-xs text-gray-400 mt-1">
            © {new Date().getFullYear()} {TENANT_NAME}. Todos os direitos reservados.
          </p>
        </div>
      </main>
    </div>
  );
}
